import React, { useState, useMemo } from "react"
import {
  columnVisibilityFeature,
  createSortedRowModel,
  flexRender,
  rowSelectionFeature,
  rowSortingFeature,
  sortFns,
  tableFeatures,
  useTable,
} from "@tanstack/react-table"
import { iconPaths } from "../../../util/iconPaths"
import ProgramTableCell from "./Table/ProgramTableCell"
// import CheckboxCell from "./Table/CheckboxCell"

// react-table v9 no longer bundles every feature — each one used here must be
// registered explicitly, with its row model in the matching slot.
const features = tableFeatures({
  columnVisibilityFeature, // provides row.getVisibleCells()
  rowSelectionFeature,
  rowSortingFeature,
  sortedRowModel: createSortedRowModel(),
  sortFns,
})

function DashboardTable({ data }) {
  const [sorting, setSorting] = useState([])
  const [rowSelection, setRowSelection] = useState({})

  const columns = useMemo(
    () => [
      {
        accessorFn: row => row.organisation?.name || "",
        id: "organisation_id",
        cell: info => info.getValue(),
        footer: props => props.column.id,
        header: "Organisation",
      },
      {
        accessorFn: row => row.unit?.name || "",
        id: "unit_id",
        cell: info => info.getValue(),
        footer: props => props.column.id,
        header: "Unit",
      },
      {
        accessorFn: row => row.name || "",
        id: "id",
        cell: info => info.getValue(),
        footer: props => props.column.id,
        header: "Program",
      },
      {
        accessorFn: row => (row.unique_id || ""),
        id: "unique_id",
        cell: info => info.getValue(),
        footer: props => props.column.id,
        header: "Unique Code",
      },
      {
        accessorFn: row => row.status,
        id: "status",
        cell: info => info.getValue(),
        footer: props => props.column.id,
        header: "Status",
      },
      {
        accessorFn: row => row.unit?.contact_name || "",
        id: "contact",
        cell: info => info.getValue(),
        footer: props => props.column.id,
        header: "Contact",
      },
      {
        accessorFn: row => row.integration_type || "",
        id: "integration_type",
        cell: info => info.getValue(),
        footer: props => props.column.id,
        header: "Integration",
      },
      {
        accessorFn: row => row.created_at,
        id: "created_at",
        cell: info => (info.getValue() ? moment(info.getValue()).format("DD MMM YYYY") : ""),
        footer: props => props.column.id,
        header: "Created Date",
      },
      {
        accessorFn: row => row.last_enrolment_date,
        id: "last_enrolment_date",
        cell: info => (info.getValue() ? moment(info.getValue()).format("DD MMM YYYY") : ""),
        footer: props => props.column.id,
        header: "Last Enrolment Date",
      },
      {
        id: "actions",
        enableSorting: false,
        cell: () => null,
        footer: props => props.column.id,
        header: "",
      },
    ],
    []
  )

  const table = useTable({
    features,
    data,
    columns,
    state: {
      rowSelection,
      sorting,
    },
    onRowSelectionChange: setRowSelection,
    onSortingChange: setSorting,
  })

  return (
    <table className="table list-table">
      <thead>
        {table.getHeaderGroups().map(headerGroup => (
          <tr key={headerGroup.id}>
            {headerGroup.headers.map(header => (
              <th key={header.id} colSpan={header.colSpan}>
                {header.isPlaceholder ? null : (
                  <div
                    {...{
                          className: header.column.getCanSort()
                            ? "cursor-pointer select-none"
                            : "",
                          onClick: header.column.getToggleSortingHandler(),
                        }}>
                    {flexRender(header.column.columnDef.header, header.getContext())}
                    {{
                      asc: <img src={iconPaths.upIcon} style={{ marginLeft: 10, width: "15px" }} />,
                      desc: <img src={iconPaths.downIcon} style={{ marginLeft: 10, width: "15px" }} />,
                    }[header.column.getIsSorted()] || null}
                  </div>
                )}
              </th>
            ))}
          </tr>
          ))}
      </thead>
      <tbody>
        {table
            .getRowModel()
            .rows
            .map(row => (
              <tr key={row.id}>
                {row.getVisibleCells().map(cell => (
                  <ProgramTableCell cell={cell} flexRender={flexRender} key={cell.id} />
                ))}
              </tr>
            ))}
      </tbody>
    </table>
  )
}

export default DashboardTable
